import { matchesBooleanFilter } from './boolean';
import { matchesDateFilter } from './date';
import { matchesNumericFilter } from './numeric';
import {
  GridFilterValueType,
  isBooleanFilterOperator,
  isDateFilterOperator,
  isMultiSelectFilterOperator,
  isNumericFilterOperator,
  isSingleSelectFilterOperator,
  isStringFilterOperator,
} from './operators';
import { matchesStringFilter } from './string';
import {
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridLogicOperator,
} from '@mui/x-data-grid-pro';
import { matchesObjectFilter } from './object';
import { matchesSingleSelectFilter } from './singleSelect';
import { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { isDateRangeType } from 'components/common/dateRangePicker';
import {
  DataGridCellValue,
  DataGridFilterValue,
  ScalarFilterValue,
  isEntityWithIdArray,
  isStringArray,
} from './types';
import { neverReached } from 'shared/utils/neverReached';

const toScalarValue = (value: DataGridFilterValue | DataGridCellValue): ScalarFilterValue => {
  if (Array.isArray(value) || value instanceof Date || isDateRangeType(value)) {
    return undefined;
  }

  return value;
};

export function matchesValueFilter(
  type: GridFilterValueType,
  operator: GridFilterOperator['value'],
  filterValue: DataGridFilterValue,
  cellValue: DataGridCellValue,
  timezone: string
): boolean {
  switch (type) {
    case GridFilterValueType.STRING:
      if (!isStringFilterOperator(operator)) return true;
      return matchesStringFilter(
        operator,
        filterValue,
        toScalarValue(cellValue)
      );
    case GridFilterValueType.NUMERIC:
      if (!isNumericFilterOperator(operator)) return true;
      return matchesNumericFilter(
        operator,
        toScalarValue(filterValue),
        toScalarValue(cellValue)
      );
    case GridFilterValueType.DATE:
      if (!isDateFilterOperator(operator)) return true;
      return matchesDateFilter(
        operator,
        isDateRangeType(filterValue) ? filterValue : undefined,
        cellValue instanceof Date ? cellValue : null,
        timezone
      );
    case GridFilterValueType.BOOLEAN:
      if (!isBooleanFilterOperator(operator)) return true;
      return matchesBooleanFilter(
        operator,
        toScalarValue(filterValue),
        toScalarValue(cellValue)
      );
    case GridFilterValueType.SINGLE_SELECT:
      if (!isSingleSelectFilterOperator(operator)) return true;
      return matchesSingleSelectFilter(
        operator,
        toScalarValue(filterValue),
        toScalarValue(cellValue)
      );
    case GridFilterValueType.MULTI_SELECT:
      if (!isMultiSelectFilterOperator(operator)) return true;
      return matchesObjectFilter(
        operator,
        isStringArray(filterValue) ? filterValue : undefined,
        isEntityWithIdArray(cellValue) ? cellValue : undefined
      );
    default:
      return neverReached(type);
  }
}

interface MatchesFilterOptions {
  fixedFilterField?: string;
}

export function matchesFilter(
  item: DirectoryModel | DocumentationModel,
  filterModel: GridFilterModel,
  valueGetters: DocumentationValueGetters,
  timezone: string,
  options: MatchesFilterOptions = {}
): boolean {
  if (!filterModel?.items?.length) return true;

  const { fixedFilterField } = options;

  const matchesItem = (filterItem: GridFilterItem): boolean => {
    const { field, value, operator } = filterItem;
    const cell = valueGetters[field]
      ? valueGetters[field](field, item)
      : item[field];
    const type = getFieldValueType(field);
    return matchesValueFilter(type, operator, value, cell, timezone);
  };

  const fixedFilters = fixedFilterField
    ? filterModel.items.filter((f) => f.field === fixedFilterField)
    : [];
  const regularFilters = fixedFilterField
    ? filterModel.items.filter((f) => f.field !== fixedFilterField)
    : filterModel.items;

  const fixedFiltersMatch =
    fixedFilters.length === 0 || fixedFilters.every(matchesItem);

  if (!fixedFiltersMatch) return false;

  if (regularFilters.length === 0) return true;

  const logicOperator = filterModel.logicOperator ?? GridLogicOperator.And;

  if (logicOperator === GridLogicOperator.Or) {
    return regularFilters.some(matchesItem);
  }

  return regularFilters.every(matchesItem);
}

function getFieldValueType(field: string): GridFilterValueType {
  switch (field) {
    case 'state':
      return GridFilterValueType.SINGLE_SELECT;
    case 'createdAt':
    case 'modifiedAt':
      return GridFilterValueType.DATE;
    case 'name':
    case 'number':
      return GridFilterValueType.STRING;
    case 'createdBy':
    case 'modifiedBy':
      return GridFilterValueType.MULTI_SELECT;
    case 'sites':
    case 'levels':
      return GridFilterValueType.MULTI_SELECT;
    case 'relatedIssuesCount':
      return GridFilterValueType.NUMERIC;
    default:
      return GridFilterValueType.STRING;
  }
}
